import { useChainId } from 'wagmi';
import { readContract } from '@wagmi/core';
import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { wagmiConfig } from '@/onchain/OnchainProviders';
import {
  getPoolTickInfoBytecode,
  getPoolTickInfoAbi,
  stateViewAddress,
} from '@generated/generated';
import { PoolKey } from '@/onchain/pools';
import { Ticks } from '@/components/LiquidityDepthChart/types';

type TickResult = {
  tick: number;
  net: bigint;
  gross: bigint;
};

type TickQueryResult = {
  v3Pool: {
    ticks: {
      tick: number;
      liquidityNet: string;
      liquidityGross: string;
      __typename: string;
    }[];
  };
};

export const QUERY_KEY_PAGINATED_TICK_QUERY = (
  chainId: number | undefined,
  poolKey: PoolKey | undefined,
  skip: number,
) => [
  'usePaginatedTickQuery',
  chainId,
  stateViewAddress[chainId as keyof typeof stateViewAddress],
  ...(poolKey ? Object.values(poolKey) : []),
  skip,
];

export const QUERY_FN_PAGINATED_TICK_QUERY = async (
  chainId: number | undefined,
  poolKey: PoolKey | undefined,
): Promise<TickQueryResult | null> => {
  const stateViewAddr = chainId
    ? stateViewAddress[chainId as keyof typeof stateViewAddress]
    : undefined;
  if (!chainId || !poolKey || !stateViewAddr) {
    return null;
  }

  const data = (await readContract(wagmiConfig, {
    abi: getPoolTickInfoAbi,
    functionName: 'getAllTicks',
    code: getPoolTickInfoBytecode,
    args: [stateViewAddress[chainId as keyof typeof stateViewAddress], poolKey],
  })) as TickResult[];

  return {
    v3Pool: {
      ticks: data.map((tick) => ({
        tick: tick.tick,
        liquidityNet: tick.net.toString(),
        liquidityGross: tick.gross.toString(),
        __typename: 'V3PoolTick',
      })),
    },
  };
};

// TODO: add pagination
export function usePaginatedTickQuery(
  poolKey: PoolKey | undefined,
  skip: number,
): {
  data: { v3Pool: { ticks: Ticks } } | undefined;
  error: Error | null;
  isLoading: boolean;
} {
  const chainId = useChainId();

  const queryOptions = useMemo(
    () => ({
      queryKey: QUERY_KEY_PAGINATED_TICK_QUERY(chainId, poolKey, skip),
      queryFn: async () => QUERY_FN_PAGINATED_TICK_QUERY(chainId, poolKey),
    }),
    [chainId, poolKey, skip],
  );

  const { data, error, isLoading } = useSuspenseQuery(queryOptions);

  return {
    data,
    error,
    isLoading,
  };
}

export default usePaginatedTickQuery;
